// Stroke-based icons ported from the Claude Design handoff. They inherit color
// via `currentColor`, so set color on the parent. Size defaults per icon.
interface IconProps {
  size?: number;
}

const svg = (size: number, strokeWidth: number, children: JSX.Element) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

export const Icon = {
  Check: ({ size = 16 }: IconProps) =>
    svg(size, 2.4, <polyline points="5 12.5 10 17.5 19 8" />),
  Lock: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 8 0v3" />
      </>,
    ),
  Unlock: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <rect x="5" y="11" width="14" height="9" rx="2" />
        <path d="M8 11V8a4 4 0 0 1 7.5-1.8" />
      </>,
    ),
  Trophy: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M8 4h8v4a4 4 0 0 1-8 0V4z" />
        <path d="M8 6H5a3 3 0 0 0 3 4" />
        <path d="M16 6h3a3 3 0 0 1-3 4" />
        <path d="M10 14v3h4v-3" />
        <path d="M8 20h8" />
      </>,
    ),
  Flame: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M12 3c1 4 4 5 4 9a4 4 0 0 1-8 0c0-2 1.5-3 2-5 .5-2-.2-3 2-4z" />
        <path d="M10.5 14a2 2 0 0 0 3.5 1.5" />
      </>,
    ),
  Refresh: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M4 9a8 8 0 0 1 13.5-3.5L20 8" />
        <path d="M20 4v4h-4" />
        <path d="M20 15a8 8 0 0 1-13.5 3.5L4 16" />
        <path d="M4 20v-4h4" />
      </>,
    ),
  Send: ({ size = 16 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M21 4 11 14" />
        <path d="M21 4l-7 17-3-7-7-3 17-7z" />
      </>,
    ),
  Bolt: ({ size = 18 }: IconProps) =>
    svg(size, 1.8, <path d="M13 3 4 14h6l-1 7 9-11h-6l1-7z" />),
  Sparkle: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <path d="M12 3v5M12 16v5M3 12h5M16 12h5M6 6l3 3M15 15l3 3M18 6l-3 3M9 15l-3 3" />,
    ),
  Link: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 1 0-5.7-5.7L11 7" />
        <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 1 0 5.7 5.7L13 17" />
      </>,
    ),
  Doc: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M7 3h8l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
        <path d="M15 3v4h4" />
        <path d="M9 12h6M9 16h6" />
      </>,
    ),
  Plane: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M21 12 3 4l3 8-3 8 18-8z" />
        <path d="M6 12h15" />
      </>,
    ),
  X: ({ size = 16 }: IconProps) =>
    svg(size, 2, <path d="M6 6l12 12M18 6 6 18" />),
  Chevron: ({ size = 16, open = false }: IconProps & { open?: boolean }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{
        transform: open ? "rotate(180deg)" : "none",
        transition: "transform 200ms",
      }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  ),
  ArrowRight: ({ size = 16 }: IconProps) =>
    svg(size, 2, <path d="M5 12h14M13 6l6 6-6 6" />),
  Play: ({ size = 16 }: IconProps) =>
    svg(size, 1.8, <path d="M8 5l11 7-11 7V5z" />),
  Book: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
        <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      </>,
    ),
  Users: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
      </>,
    ),
  TrendUp: ({ size = 16 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M3 17 9 11l4 4 8-8" />
        <path d="M14 4h7v7" />
      </>,
    ),
  Dollar: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M12 2v20" />
        <path d="M17 6.5C17 4.5 14.7 3 12 3S7 4.5 7 6.5 9.3 10 12 10s5 1.5 5 3.5S14.7 17 12 17s-5-1.5-5-3.5" />
      </>,
    ),
  Eye: ({ size = 16 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" />
        <circle cx="12" cy="12" r="3" />
      </>,
    ),
  CheckList: ({ size = 18 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <polyline points="3 6 4.5 7.5 7 5" />
        <polyline points="3 12 4.5 13.5 7 11" />
        <polyline points="3 18 4.5 19.5 7 17" />
        <path d="M10 6h11M10 12h11M10 18h11" />
      </>,
    ),
  Search: ({ size = 16 }: IconProps) =>
    svg(
      size,
      2,
      <>
        <circle cx="11" cy="11" r="7" />
        <path d="M21 21l-4.3-4.3" />
      </>,
    ),
  Plus: ({ size = 16 }: IconProps) =>
    svg(size, 2.2, <path d="M12 5v14M5 12h14" />),
  Diamond: ({ size = 16 }: IconProps) =>
    svg(
      size,
      1.8,
      <>
        <path d="M6 3h12l4 6-10 12L2 9z" />
        <path d="M2 9h20M12 3 8 9l4 12 4-12-4-6" />
      </>,
    ),
  Hourglass: ({ size = 16 }: IconProps) =>
    svg(
      size,
      1.8,
      <path d="M5 22h14M5 2h14M17 22v-4.17a2 2 0 0 0-.59-1.42L12 12l-4.41 4.41A2 2 0 0 0 7 17.83V22M7 2v4.17a2 2 0 0 0 .59 1.42L12 12l4.41-4.41A2 2 0 0 0 17 6.17V2" />,
    ),
};
